import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';

import EnhancedTable, { EnhancedTableProps, EnhancedTableRow } from '#components/EnhancedTable';
import {
  setOrderBag,
  setOrderByBag,
  setPageIndexBag,
} from './reportTable.slice';

interface ReportTableCollection {
  id: string;
  name: string;
  inactivated?: boolean;
}

interface ReportData {
  rows: EnhancedTableRow[];
  total: number;
  collectionId?: string;
  allSizes?: Array<string | number>;
  [key: string]: unknown;
}

interface ReportTableState {
  orderBag: Record<string, unknown>;
  orderByBag: Record<string, unknown>;
  pageIndexBag: Record<string, unknown>;
}

type ReportTablePropsBag = Partial<Omit<EnhancedTableProps, 'rows'>> & {
  headers: EnhancedTableProps['headers'];
  name?: string;
};

interface GetReportResult {
  data?: unknown;
  error?: unknown;
}

interface InventoryReportTableProps {
  collection: ReportTableCollection;
  report?: ReportData | undefined;
  tableProps: ReportTablePropsBag;
  getReport: (arg: Record<string, unknown>) => Promise<GetReportResult>;
  queryOptions: Record<string, unknown>;
}

export default function InventoryReportTable(props: InventoryReportTableProps) {
  const {
    collection,
    report: initialReport,
    tableProps,
    getReport,
    queryOptions,
  } = props;
  const { t } = useTranslation('inventory');

  const [skip, setSkip] = useState(true);
  const [report, setReport] = useState(initialReport);
  const [showInProduction, setShowInProduction] = useState(true);

  const order = useSelector(
    (state: { reportTable: ReportTableState }) => state.reportTable.orderBag[collection.id],
  ) as 'asc' | 'desc' | undefined;
  const orderBy = useSelector(
    (state: { reportTable: ReportTableState }) => state.reportTable.orderByBag[collection.id],
  ) as string | undefined;
  const pageIndex = useSelector(
    (state: { reportTable: ReportTableState }) => state.reportTable.pageIndexBag[collection.id],
  ) as number | undefined;

  const setOrder = (value: string) => setOrderBag({
    collection: collection.id,
    value,
  });

  const setOrderBy = (value: string) => setOrderByBag({
    collection: collection.id,
    value,
  });

  const setPageIndex = (value: number) => setPageIndexBag({
    collection: collection.id,
    value,
  });

  useEffect(() => {
    setReport(initialReport);
  }, [initialReport]);

  useEffect(() => {
    if (skip) {
      setSkip(false);
      return;
    }

    void getReport({
      order,
      orderBy,
      page: pageIndex,
      collections: [collection],
      showInProduction,
      ...queryOptions,
    }).then((res) => {
      // TODO handle res.error
      if (res.error) {
        return;
      }

      const { reports } = res.data as { reports: ReportData[] };
      const newReport = reports.find((r) => r.collectionId === collection.id);
      setReport(newReport);
    });
  }, [order, orderBy, pageIndex, showInProduction]);

  if (!report) {
    return null;
  }

  const {
    rows,
    total,
  } = report;

  return (
    <>
      <Grid
        container
        direction="row"
        spacing={3}
        sx={{
          justifyContent: 'flex-start',
          alignItems: 'center',
          mt: 1,
        }}
      >
        <Grid>
          {t('collectionPrefix')}
          {' '}
          {collection.name}
          {collection.inactivated && (
            <Box sx={{ fontStyle: 'italic', display: 'inline', color: 'text.secondary' }}>
              {' '}
              {t('inactiveMarker')}
            </Box>
          )}
        </Grid>
        {
          (tableProps.name === 'inventory-report-table') && (
            <Grid>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={showInProduction}
                    onChange={(event, value) => setShowInProduction(value)}
                  />
                )}
                label={t('showInProduction')}
              />
            </Grid>
          )
        }
      </Grid>
      <EnhancedTable
        {...tableProps}
        {...{
          order,
          setOrder,
          orderBy,
          setOrderBy,

          pageIndex: pageIndex || 0,
          rowCount: total,
          setPageIndex,
          rows,
        }}
      />
    </>
  );
}
